import type { Project, RateOrInherit, RateSpec, Scheduled } from '@finsim/model';
import { CalendarRange } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPercent } from '../../../shared/lib/format';
import { useLocale } from '../../../shared/lib/i18n/use-locale';
import { Button, EditOnly, Field, IconButton, Tooltip } from '../../../shared/ui';
import { cpiPath, rateAt } from '../lib/rate-preview';
import { RateValueEditor, type RateKind } from './rate-value';
import { useDescribeRate } from './use-describe-rate';
import { ScheduleEditor } from './schedule-editor';

interface ScheduledRateFieldProps<V extends RateOrInherit> {
  label: string;
  value: Scheduled<V>;
  onCommit: (value: Scheduled<V>) => void;
  project: Project;
  kinds: readonly RateKind[];
  /** Narrows edited values back to V (the kinds offered guarantee it). */
  narrow: (value: RateOrInherit) => value is V;
  inheritLabel?: string;
  /** Path an `inherit` period resolves to (preview only). */
  inherited?: ArrayLike<number>;
  hint?: string;
}

/**
 * A rate that is a single value by default, with "Vary over time" opening the schedule editor
 * (CLAUDE.md "Time-varying values"). Scheduled rates show a summary; click it to edit.
 */
function ScheduledRateField<V extends RateOrInherit>({
  label,
  value,
  onCommit,
  project,
  kinds,
  narrow,
  inheritLabel,
  inherited,
  hint,
}: ScheduledRateFieldProps<V>) {
  const { t } = useTranslation();
  const locale = useLocale();
  const describe = useDescribeRate();
  const [editing, setEditing] = useState(false);
  const cpi = useMemo(() => cpiPath(project), [project]);
  const { start, horizonMonths } = project.assumptions;

  const accept = (v: RateOrInherit): V | null => (narrow(v) ? v : null);
  const summary =
    value.kind === 'schedule'
      ? t('schedule.summary', {
          count: value.segments.length,
          from: describe(value.segments[0]?.value ?? { kind: 'inherit' }, inheritLabel),
          to: describe(value.segments.at(-1)?.value ?? { kind: 'inherit' }, inheritLabel),
        })
      : null;

  return (
    <>
      <Field label={label} {...(hint === undefined ? {} : { hint })}>
        {() =>
          value.kind === 'constant' ? (
            <div className="flex items-start gap-1">
              <div className="min-w-0 flex-1">
                <RateValueEditor
                  label={label}
                  kinds={kinds}
                  value={value.value}
                  {...(inheritLabel === undefined ? {} : { inheritLabel })}
                  onChange={(v) => {
                    const next = accept(v);
                    if (next) onCommit({ kind: 'constant', value: next });
                  }}
                />
              </div>
              <EditOnly>
                <Tooltip content={t('schedule.varyOverTime')}>
                  <IconButton
                    label={`${t('schedule.varyOverTime')} — ${label}`}
                    onClick={() => {
                      setEditing(true);
                    }}
                  >
                    <CalendarRange />
                  </IconButton>
                </Tooltip>
              </EditOnly>
            </div>
          ) : (
            <Button
              variant="secondary"
              className="h-auto min-h-7 w-full justify-start py-1 text-left whitespace-normal"
              leadingIcon={<CalendarRange />}
              aria-label={`${label}: ${summary ?? ''}`}
              onClick={() => {
                setEditing(true);
              }}
            >
              <span className="text-xs">{summary}</span>
            </Button>
          )
        }
      </Field>
      <ScheduleEditor<V>
        open={editing}
        onOpenChange={setEditing}
        title={t('schedule.title', { label })}
        value={value}
        start={start}
        horizon={horizonMonths}
        onSave={onCommit}
        renderValue={(v, onChange, periodLabel) => (
          <RateValueEditor
            label={periodLabel}
            kinds={kinds}
            value={v}
            {...(inheritLabel === undefined ? {} : { inheritLabel })}
            onChange={(next) => {
              const narrowed = accept(next);
              if (narrowed) onChange(narrowed);
            }}
          />
        )}
        toNumber={(v, t) => rateAt(v, t, cpi, inherited ?? cpi)}
        formatPreview={(n) => formatPercent(n, locale, 1).replace('-', '−')}
      />
    </>
  );
}

const isRateSpec = (v: RateOrInherit): v is RateSpec => v.kind !== 'inherit';
const isRateOrInherit = (value: RateOrInherit): value is RateOrInherit => Boolean(value);

export interface RateFieldProps {
  label: string;
  value: Scheduled<RateSpec>;
  onCommit: (value: Scheduled<RateSpec>) => void;
  project: Project;
  /** CPI itself cannot be CPI-relative. */
  allowCpiRelative?: boolean;
  hint?: string;
}

export function RateField({ allowCpiRelative = true, ...props }: RateFieldProps) {
  return (
    <ScheduledRateField<RateSpec>
      {...props}
      kinds={
        allowCpiRelative ? ['fixed', 'cpiRelative', 'distribution'] : ['fixed', 'distribution']
      }
      narrow={isRateSpec}
    />
  );
}

export interface RateOrInheritFieldProps {
  label: string;
  /** `undefined` means "inherit everywhere". */
  value: Scheduled<RateOrInherit> | undefined;
  onCommit: (value: Scheduled<RateOrInherit> | undefined) => void;
  project: Project;
  inheritLabel: string;
  inherited?: ArrayLike<number>;
  hint?: string;
}

/** An override that can defer to the next level (category default → global CPI) per period. */
export function RateOrInheritField({ value, onCommit, ...props }: RateOrInheritFieldProps) {
  return (
    <ScheduledRateField<RateOrInherit>
      {...props}
      value={value ?? { kind: 'constant', value: { kind: 'inherit' } }}
      onCommit={(next) => {
        // "Inherit everywhere" is stored as no override at all.
        onCommit(next.kind === 'constant' && next.value.kind === 'inherit' ? undefined : next);
      }}
      kinds={['inherit', 'fixed', 'cpiRelative', 'distribution']}
      narrow={isRateOrInherit}
    />
  );
}
